import mapboxgl, { GeoJSONSource } from "mapbox-gl";
import { store } from "../../../store";
import { addAllLayersInOrder, setHighlightsWithPopup } from "./layers";
import { initSources, tripsSources } from "./sources";
import * as neighborSources from "../neighbors/sources";
import * as neighborLayers from "../neighbors/layers";

import { loadIcons } from "../../map/icons";
import { makeMap } from "../../map/map";

import {
    point,
    multilineString,
    lineString,
    CoordinateObject,
    emptyGeojsonObject,
} from "../../map/convert/geojson";
import { SelectedTrip, TripGeoDetails } from "../../../interfaces";
import { GoCancelsRow } from "../../../api/models/GoCancelsRow";
import * as uuid from "uuid";

export const initMap = async (
    mapContainerName: string
): Promise<mapboxgl.Map> => {
    const map = makeMap(mapContainerName);

    await map.on("load", async () => {
        await initSources(map);
        await neighborSources.initSources(map);
        await loadIcons(map);
        await addAllLayersInOrder(map);
        await neighborLayers.addAllLayersInOrder(map);
    });

    map.on("mouseover", () => {
        map.getCanvas().style.cursor = "initial";
    });
    map.on("mouseleave", () => {
        map.getCanvas().style.cursor = "";
    });

    neighborLayers.setHighlightsWithPopup(map);
    setHighlightsWithPopup(map);

    return map;
};

const boardingColor = (boardingType?: string) => {
    switch (boardingType) {
        case "go":
            return "blue";
        case "cruising":
            return "green";
        case "tel":
            return "purple";
        case "stand":
            return "red";
        default:
            return "transparent";
    }
};

const convert = (
    report: TripGeoDetails | null,
    boardingType: string,
    boardingTs: string
) => {
    let unladenStartPt = emptyGeojsonObject();
    let deliverStartPt = emptyGeojsonObject();
    let naviShape = emptyGeojsonObject();
    let unladenShape = emptyGeojsonObject();

    if (!report) {
        return { unladenStartPt, deliverStartPt, naviShape, unladenShape };
    }

    if (report.unladenStartLoc) {
        const loc = report.unladenStartLoc as any;
        const _up = [
            {
                ...report,
                coordinates: loc.shape,
                id: uuid.v4(),
            },
        ];
        unladenStartPt = point(_up as CoordinateObject[]);
    }

    if (report.deliverStartLoc) {
        console.debug(
            "COLOR for deliver:",
            boardingColor(boardingType),
            boardingType
        );
        const loc = report.deliverStartLoc as any;
        const _up = [
            {
                ...report,
                boardingType,
                boardingTs,
                coordinates: loc.shape,
                id: uuid.v4(),
                color: boardingColor(boardingType),
            },
        ];
        deliverStartPt = point(_up as CoordinateObject[]);
    }

    if (report.naviShape) {
        const loc = report.naviShape as any;
        const _up = [
            {
                ...report,
                color: "lightgreen",
                coordinates: loc.shape,
                id: uuid.v4(),
            },
        ];

        if (loc.type == "LineString") {
            naviShape = lineString(_up as CoordinateObject[]);
        }

        if (loc.type == "MultiLineString") {
            naviShape = multilineString(_up as CoordinateObject[]);
        }
    }

    if (report.unladenShape) {
        const loc = report.unladenShape as any;
        const _up = [
            {
                ...report,
                color: "black",
                coordinates: loc.shape,
                id: uuid.v4(),
            },
        ];

        if (loc.type == "LineString") {
            unladenShape = lineString(_up as CoordinateObject[]);
        }

        if (loc.type == "MultiLineString") {
            unladenShape = multilineString(_up as CoordinateObject[]);
        }
    }

    return { unladenStartPt, deliverStartPt, naviShape, unladenShape };
};

const getZoomLoc = (report?: TripGeoDetails | null) => {
    if (!report) {
        return;
    }

    if (report.unladenStartLoc as any) {
        return (report.unladenStartLoc as any).shape;
    }

    if (report.deliverStartLoc as any) {
        return (report.deliverStartLoc as any).shape;
    }

    if (report.naviShape as any) {
        if ((report.naviShape as any).shape) {
            return (report.naviShape as any).shape[0][0];
        }
    }

    if (report.unladenShape as any) {
        if ((report.unladenShape as any).shape) {
            return (report.unladenShape as any).shape[0][0];
        }
    }

    return null;
};

export const show = async (
    map: mapboxgl.Map,
    report?: SelectedTrip | null,
    keepCenter?: boolean
): Promise<void> => {
    if (!report) {
        console.log("TRIPMAP: No report was selected");
        return;
    }

    const zoomloc = getZoomLoc(report.details);
    if (zoomloc) {
        if (!keepCenter) {
            map.setCenter(zoomloc);
            map.setZoom(13)
        }
    }

    const boardingType = report.boardingType ? report.boardingType : "";
    const boardingTs = report.boardingTs ? report.boardingTs : "";

    const { unladenStartPt, deliverStartPt, naviShape, unladenShape } = convert(
        report.details,
        boardingType,
        boardingTs
    );

    try {
        (map.getSource(
            tripsSources.unladenStartPtSrc
        ) as GeoJSONSource).setData(unladenStartPt);
        (map.getSource(
            tripsSources.deliverStartLocSrc
        ) as GeoJSONSource).setData(deliverStartPt);
        (map.getSource(tripsSources.naviShapeSrc) as GeoJSONSource).setData(
            naviShape
        );
        (map.getSource(tripsSources.unladenShapeSrc) as GeoJSONSource).setData(
            unladenShape
        );
    } catch (err) {
        console.log("Error adding data to map..", err);
        await map.on("load", async () => {
            console.log("Loading Map...");
            (map.getSource(
                tripsSources.unladenStartPtSrc
            ) as GeoJSONSource).setData(unladenStartPt);
            (map.getSource(
                tripsSources.deliverStartLocSrc
            ) as GeoJSONSource).setData(deliverStartPt);
            (map.getSource(tripsSources.naviShapeSrc) as GeoJSONSource).setData(
                naviShape
            );
            (map.getSource(
                tripsSources.unladenShapeSrc
            ) as GeoJSONSource).setData(unladenShape);
        });
    }
};

export const showGoCancels = async (map: mapboxgl.Map): Promise<void> => {
    const state = store.getState();
    const go_cancels = state.trips.goCancels;
    console.debug("go_cancel data:", go_cancels, map);
    if (!go_cancels) {
        return;
    }

    let goCancelsJson: any = null;

    const _goCancelsJson = go_cancels.map((x: GoCancelsRow) => {
        const loc = x.goCancelLocCoords as any;
        return {
            ...x,
            coordinates: loc.shape,
            id: uuid.v4(),
        };
    });
    goCancelsJson = point(_goCancelsJson as CoordinateObject[]);

    await map.on("load", async () => {
        (map.getSource(tripsSources.goCancelLocSrc) as GeoJSONSource).setData(
            goCancelsJson
        );
    });
};
